<template>
    <div :class="containerClass">
        <CompassTooltip
            v-if="showBackButton"
            :content="tooltipContent"
            :placement="CompassEnums.TooltipPosition.BOTTOM_START"
            :arrow="false"
            :disabled="!showTooltip"
        >
            <CompassIconButton icon="caret-left" @click="handleClick" />
        </CompassTooltip>
        <CompassColoredIcon
            v-if="hasPrefixColoredIcon"
            class="heading__icon"
            :icon="prefixColoredIcon.icon"
            :color="prefixColoredIcon.color"
            :size="iconSize"
        />
        <component :is="variant" :class="fontClass">
            {{ labelTitle }}
        </component>
        <slot />
    </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { Translator } from 'types/injects/translator.type';

type HeadingVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface PrefixColoredIcon {
    icon?: string;
    color?: string;
}

interface Props {
    labelTitle: string;
    showTooltip?: boolean;
    labelTooltip?: string;
    variant?: HeadingVariant;
    prefixColoredIcon?: PrefixColoredIcon;
    showBackButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    showTooltip: true,
    labelTooltip: '',
    variant: 'h1',
    prefixColoredIcon: () => ({}),
    showBackButton: false,
});

const emit = defineEmits<{
    'go-back': [];
}>();

const $t = inject('$t') as Translator;

const tooltipContent = computed<string>(() => {
    return props.labelTooltip || $t.__('common.actions.back');
});

const containerClass = computed<string[]>(() => {
    return ['heading', `heading--${props.variant.toLowerCase()}`];
});

const fontClass = computed<string[]>(() => {
    return ['heading__title', `typographies-heading-${props.variant.toLowerCase()}`];
});

const iconSize = computed<string>(() => {
    return props.variant === 'h2'
        ? CompassEnums.ColoredIconSize.LARGE
        : CompassEnums.ColoredIconSize.MEDIUM;
});

const hasPrefixColoredIcon = computed<boolean>(() => {
    return Object.keys(props.prefixColoredIcon).length > 0;
});

const handleClick = (): void => {
    emit('go-back');
};
</script>

<style lang="scss" scoped>
.heading {
    display: flex;
    align-items: center;
    gap: var(--spacing-0);

    &--h1 {
        margin: 0 0 var(--spacing-4);
        flex-wrap: wrap;
    }

    &--h2 {
        margin: 0 0 var(--spacing-3);

        .heading__icon {
            margin-right: var(--spacing-1);
        }
    }

    &--h3,
    &--h4 {
        margin: 0 0 var(--spacing-1);

        .heading__icon {
            margin-right: var(--spacing-0);
        }
    }

    &--h5,
    &--h6 {
        margin: 0 0 var(--spacing-0);
    }

    &__icon {
        flex-shrink: 0;
    }

    &__title {
        margin: 0;
    }
}
</style>
